import React, { useMemo, useCallback, useState } from 'react'
import { GoogleMap, OverlayView } from '@react-google-maps/api'
import { Box } from '@chakra-ui/react'
import { FaMapMarkerAlt } from 'react-icons/fa'
import { UserAround } from '../../../../../api/api_hooks/user/useGetUsersAroundPoint'
import { chakra } from '@chakra-ui/react'

import type { FC } from 'react'
const ChakraFaMapMarkerAlt = chakra(FaMapMarkerAlt as FC)

interface SearchParams {
  location: {
    lat: string
    lon: string
  }
  radius: number
}

interface MapViewProps {
  searchParams: SearchParams
  users: UserAround[]
  selectedUser?: UserAround | null
}

const MapView: React.FC<MapViewProps> = ({
  searchParams,
  users,
  selectedUser,
}) => {
  // Store map instance reference (prefixed with _ to indicate intentionally unused)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_map, setMap] = useState<google.maps.Map | null>(null)

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map)
    if (selectedUser) {
      map.panTo({
        lat: selectedUser.location.coordinates[1],
        lng: selectedUser.location.coordinates[0],
      })
    }
  }, [selectedUser])

  const onUnmount = useCallback(() => {
    setMap(null)
  }, [])

  const center = useMemo(() => {
    const lat = parseFloat(searchParams.location.lat)
    const lon = parseFloat(searchParams.location.lon)

    if (selectedUser) {
      return {
        lat: selectedUser.location.coordinates[1],
        lng: selectedUser.location.coordinates[0],
      }
    }

    // By default, center the map on Paris
    return !isNaN(lat) && !isNaN(lon)
      ? { lat, lng: lon }
      : { lat: 48.8566, lng: 2.3522 }
  }, [searchParams.location.lat, searchParams.location.lon, selectedUser])

  const mapOptions = {
    disableDefaultUI: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    zoomControl: true,
  }

  return (
    <GoogleMap
      mapContainerStyle={{
        width: '100%',
        height: '100%',
        minHeight: '340px',
      }}
      center={center}
      zoom={12}
      options={mapOptions}
      onLoad={onLoad}
      onUnmount={onUnmount}
    >
      {users.map((user) => {
        const userLat = user.location.coordinates[1]
        const userLng = user.location.coordinates[0]
        const isSelected = user.id === selectedUser?.id

        return (
          <OverlayView
            key={user.email}
            position={{ lat: userLat, lng: userLng }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <Box
              color={isSelected ? 'red.500' : 'gray.600'}
              fontSize={isSelected ? '2.5rem' : '2rem'}
              transform="translate(-50%, -100%)"
            >
              <ChakraFaMapMarkerAlt />
            </Box>
          </OverlayView>
        )
      })}
    </GoogleMap>
  )
}

export default MapView
